import {
  CreditCard,
  Invalid,
  PassengerError,
  Payment,
  PaymentVerifyResultEnum,
  PersonId,
  FiatPrice,
  RewardsPrice,
  CallState,
  PaymentSessionToken,
  PaymentError,
  Prices,
  CarPriceQuote,
  GroundBookingResult,
  IPerson,
  SELECT_PAYMENT,
  TravelWalletOffer,
  TravelWalletCredit,
  ICorpPerson,
  EarnResponse,
} from "redmond";

import * as H from "history";
import { PurchaseError } from "@b2bportal/purchase-api";
import * as actionTypes from "./constants";
import { FlightInfoValues } from "../utils/bookingFields";
import { IUpdateUserPassengerRequest } from "../../../api/v1/book/user-passengers/updateUserPassenger";
import { IDeleteUserPassengerRequest } from "../../../api/v1/book/user-passengers/deleteUserPassenger";
import { trackEvent } from "../../../api/v1/analytics/trackEvent";
import { ICarBookExperiments } from "../reducer";

export const fetchUserPassengers = (): IFetchUserPassengers => ({
  type: actionTypes.FETCH_USER_PASSENGERS,
});

export interface IFetchUserPassengers {
  type: actionTypes.FETCH_USER_PASSENGERS;
}

export const updateUserPassenger = (
  payload: IUpdateUserPassengerArgs,
  defaultTravelerId?: string,
  entryPoint?: string
): IUpdateUserPassenger => ({
  type: actionTypes.UPDATE_USER_PASSENGER,
  payload,
  defaultTravelerId,
  entryPoint,
});

export interface IUpdateUserPassenger {
  type: actionTypes.UPDATE_USER_PASSENGER;
  payload: IUpdateUserPassengerArgs;
  defaultTravelerId?: string;
  entryPoint?: string;
}

export interface IUpdateUserPassengerArgs extends IUpdateUserPassengerRequest {}

export const deleteUserPassenger = (
  payload: IDeleteUserPassengerArgs
): IDeleteUserPassenger => ({
  type: actionTypes.DELETE_USER_PASSENGER,
  payload,
});

export interface IDeleteUserPassenger {
  type: actionTypes.DELETE_USER_PASSENGER;
  payload: IDeleteUserPassengerArgs;
}

export interface IDeleteUserPassengerArgs extends IDeleteUserPassengerRequest {}

export const setUserPassengers = (args: {
  userPassengerCallState: CallState;
  userPassengers: IPerson[] | ICorpPerson[];
}): ISetUserPassengers => ({
  type: actionTypes.SET_USER_PASSENGERS,
  ...args,
});

export interface ISetUserPassengers {
  type: actionTypes.SET_USER_PASSENGERS;
  userPassengerCallState: CallState;
  userPassengers: IPerson[] | ICorpPerson[];
}

export const setUserSelectedPassengerIds = (args: {
  userSelectedPassengerIds: PersonId[];
}): ISetUserSelectedPassengerIds => ({
  type: actionTypes.SET_USER_SELECTED_PASSENGER_IDS,
  ...args,
});

export interface ISetUserSelectedPassengerIds {
  type: actionTypes.SET_USER_SELECTED_PASSENGER_IDS;
  userSelectedPassengerIds: PersonId[];
}

export const setUserPassengersCallStateFailed =
  (): ISetUserPassengersCallStateFailed => ({
    type: actionTypes.SET_USER_PASSENGERS_CALL_STATE_FAILED,
  });

export interface ISetUserPassengersCallStateFailed {
  type: actionTypes.SET_USER_PASSENGERS_CALL_STATE_FAILED;
}

export const acknowledgeUserPassengerFailure =
  (): IAcknowledgeUpdateUserPassengerFailure => ({
    type: actionTypes.ACKNOWLEDGE_UPDATE_USER_PASSENGER_FAILURE,
  });

export interface IAcknowledgeUpdateUserPassengerFailure {
  type: actionTypes.ACKNOWLEDGE_UPDATE_USER_PASSENGER_FAILURE;
}

export const acknowledgePriceDifference = (): IAcknowledgePriceDifference => ({
  type: actionTypes.ACKNOWLEDGE_PRICE_DIFFERENCE,
});

export interface IAcknowledgePriceDifference {
  type: actionTypes.ACKNOWLEDGE_PRICE_DIFFERENCE;
}

export interface ISetContactInfo {
  type: actionTypes.SET_CONTACT_INFO;
  email: string;
  phoneNumber: string;
}

export const setContactInfo = (
  email: string,
  phoneNumber: string
): ISetContactInfo => ({
  type: actionTypes.SET_CONTACT_INFO,
  email,
  phoneNumber,
});

export const fetchPaymentMethod = (paymentId: string): IFetchPaymentMethod => ({
  type: actionTypes.FETCH_PAYMENT_METHOD,
  paymentId,
});

export interface IFetchPaymentMethod {
  type: actionTypes.FETCH_PAYMENT_METHOD;
  paymentId: string;
}

export const listPaymentMethods = (): IListPaymentMethods => ({
  type: actionTypes.LIST_PAYMENT_METHODS,
});

export interface IListPaymentMethods {
  type: actionTypes.LIST_PAYMENT_METHODS;
}

export const verifyPaymentMethod = (
  payload: IVerifyPaymentMethodArgs,
  accountId: string,
  isVCN: boolean
): IVerifyPaymentMethod => ({
  type: actionTypes.VERIFY_PAYMENT_METHOD,
  payload,
  accountId,
  isVCN,
});

export interface IVerifyPaymentMethod {
  type: actionTypes.VERIFY_PAYMENT_METHOD;
  payload: IVerifyPaymentMethodArgs;
  accountId: string;
  isVCN: boolean;
}

export interface IVerifyPaymentMethodArgs {
  token: string;
}

export const setPaymentMethods = (args: {
  paymentMethods: Payment[];
}): ISetPaymentMethods => ({
  type: actionTypes.SET_PAYMENT_METHODS,
  ...args,
});

export interface ISetPaymentMethods {
  type: actionTypes.SET_PAYMENT_METHODS;
  paymentMethods: Payment[];
}

export const setPaymentMethod = (args: {
  paymentMethod?: CreditCard;
}): ISetPaymentMethod => ({
  type: actionTypes.SET_PAYMENT_METHOD,
  ...args,
});

export interface ISetPaymentMethod {
  type: actionTypes.SET_PAYMENT_METHOD;
  paymentMethod?: CreditCard;
}

export const resetPaymentCardSelectedAccounts =
  (): IResetPaymentCardSelectedAccounts => ({
    type: actionTypes.RESET_PAYMENT_CARD_SELECTED_ACCOUNTS,
  });

export interface IResetPaymentCardSelectedAccounts {
  type: actionTypes.RESET_PAYMENT_CARD_SELECTED_ACCOUNTS;
}
export const setSelectedPaymentMethodId = (args: {
  paymentMethodId: string;
  accountId?: string;
}): ISetSelectedPaymentMethodId => {
  trackEvent({
    eventName: SELECT_PAYMENT,
    properties: {},
  });
  return {
    type: actionTypes.SET_SELECTED_PAYMENT_METHOD_ID,
    ...args,
  };
};

export interface ISetSelectedPaymentMethodId {
  type: actionTypes.SET_SELECTED_PAYMENT_METHOD_ID;
  paymentMethodId: string;
  accountId?: string;
}

export interface ISetPaymentMethods {
  type: actionTypes.SET_PAYMENT_METHODS;
  paymentMethods: Payment[];
}

export const deletePaymentMethod = (args: {
  paymentId: string;
}): IDeletePaymentMethod => ({
  type: actionTypes.DELETE_PAYMENT_METHOD,
  ...args,
});

export interface IDeletePaymentMethod {
  type: actionTypes.DELETE_PAYMENT_METHOD;
  paymentId: string;
}

export const deletePaymentMethodCallStateFailed =
  (): IDeletePaymentMethodCallStateFailed => ({
    type: actionTypes.DELETE_PAYMENT_METHOD_CALL_STATE_FAILED,
  });

export interface IDeletePaymentMethodCallStateFailed {
  type: actionTypes.DELETE_PAYMENT_METHOD_CALL_STATE_FAILED;
}

export const deletePaymentMethodCallStateSuccess =
  (): IDeletePaymentMethodCallStateSuccess => ({
    type: actionTypes.DELETE_PAYMENT_METHOD_CALL_STATE_SUCCESS,
  });

export interface IDeletePaymentMethodCallStateSuccess {
  type: actionTypes.DELETE_PAYMENT_METHOD_CALL_STATE_SUCCESS;
}

export const setPaymentMethodsCallStateFailed =
  (): ISetPaymentMethodsCallStateFailed => ({
    type: actionTypes.SET_PAYMENT_METHODS_CALL_STATE_FAILED,
  });

export interface ISetPaymentMethodsCallStateFailed {
  type: actionTypes.SET_PAYMENT_METHODS_CALL_STATE_FAILED;
}

export const setPaymentMethodCallStateFailed =
  (): ISetPaymentMethodCallStateFailed => ({
    type: actionTypes.SET_PAYMENT_METHOD_CALL_STATE_FAILED,
  });

export interface ISetPaymentMethodCallStateFailed {
  type: actionTypes.SET_PAYMENT_METHOD_CALL_STATE_FAILED;
}

export const verifyPaymentMethodCallStateFailed = (
  result: PaymentVerifyResultEnum
): IVerifyPaymentMethodCallStateFailed => ({
  type: actionTypes.VERIFY_PAYMENT_METHOD_CALL_STATE_FAILED,
  result,
});

export interface IVerifyPaymentMethodCallStateFailed {
  type: actionTypes.VERIFY_PAYMENT_METHOD_CALL_STATE_FAILED;
  result: PaymentVerifyResultEnum;
}

export const verifyPaymentMethodCallStateSuccess =
  (): IVerifyPaymentMethodCallStateSuccess => ({
    type: actionTypes.VERIFY_PAYMENT_METHOD_CALL_STATE_SUCCESS,
  });

export interface IVerifyPaymentMethodCallStateSuccess {
  type: actionTypes.VERIFY_PAYMENT_METHOD_CALL_STATE_SUCCESS;
}

export const setSession = (token: PaymentSessionToken): ISetSession => ({
  type: actionTypes.SET_SESSION,
  token,
});

export interface ISetSession {
  type: actionTypes.SET_SESSION;
  token: PaymentSessionToken;
}

export const clearSession = (): IClearSession => ({
  type: actionTypes.CLEAR_SESSION,
});

export interface IClearSession {
  type: actionTypes.CLEAR_SESSION;
}

export const schedulePriceQuote = (
  history: H.History,
  agentFee?: number,
  pollQuoteOnly?: boolean
): ISchedulePriceQuote => ({
  type: actionTypes.SCHEDULE_PRICE_QUOTE,
  history,
  agentFee,
  pollQuoteOnly,
});

export interface ISchedulePriceQuote {
  type: actionTypes.SCHEDULE_PRICE_QUOTE;
  history: H.History;
  agentFee?: number;
  pollQuoteOnly?: boolean;
}

export const setSchedulePriceQuoteCallStateSuccess =
  (): ISetSchedulePriceQuoteCallStateSuccess => ({
    type: actionTypes.SCHEDULE_PRICE_QUOTE_CALL_STATE_SUCCESS,
  });

export interface ISetSchedulePriceQuoteCallStateSuccess {
  type: actionTypes.SCHEDULE_PRICE_QUOTE_CALL_STATE_SUCCESS;
}

export const setSchedulePriceQuoteCallStateFailed = (
  invalid: Invalid
): ISetSchedulePriceQuoteCallStateFailure => ({
  type: actionTypes.SCHEDULE_PRICE_QUOTE_CALL_STATE_FAILED,
  invalid,
});

export interface ISetSchedulePriceQuoteCallStateFailure {
  type: actionTypes.SCHEDULE_PRICE_QUOTE_CALL_STATE_FAILED;
  invalid: Invalid;
}

export const pollPriceQuote = (
  agentFee?: number,
  pollQuoteOnly?: boolean
): IPollPriceQuote => ({
  type: actionTypes.POLL_PRICE_QUOTE,
  agentFee,
  pollQuoteOnly,
});

export interface IPollPriceQuote {
  type: actionTypes.POLL_PRICE_QUOTE;
  agentFee?: number;
  pollQuoteOnly?: boolean;
}

export const setPollPriceQuoteCallStateSuccess =
  (): IPollPriceQuoteCallStateSuccess => ({
    type: actionTypes.SET_POLL_PRICE_QUOTE_CALL_STATE_SUCCESS,
  });

export interface IPollPriceQuoteCallStateSuccess {
  type: actionTypes.SET_POLL_PRICE_QUOTE_CALL_STATE_SUCCESS;
}

export const setPollPriceQuoteCallStateFailure = (
  errors: PaymentError[] | PurchaseError[]
): ISetPollPriceQuoteCallStateFailure => ({
  type: actionTypes.SET_POLL_PRICE_QUOTE_CALL_STATE_FAILED,
  errors,
});

export interface ISetPollPriceQuoteCallStateFailure {
  type: actionTypes.SET_POLL_PRICE_QUOTE_CALL_STATE_FAILED;
  errors: PaymentError[] | PurchaseError[];
}

export const setPriceQuote = (
  priceQuote: CarPriceQuote | null
): ISetPriceQuote => ({
  type: actionTypes.SET_PRICE_QUOTE,
  priceQuote,
});

export interface ISetPriceQuote {
  type: actionTypes.SET_PRICE_QUOTE;
  priceQuote: CarPriceQuote | null;
}

export const pollPassengerValidation = (): IPollPassengerValidation => ({
  type: actionTypes.POLL_PASSENGER_VALIDATION,
});

export interface IPollPassengerValidation {
  type: actionTypes.POLL_PASSENGER_VALIDATION;
}

export const setPollPassengerValidationCallStatePending =
  (): IPollPassengerValidationCallStatePending => ({
    type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_PENDING,
  });

export interface IPollPassengerValidationCallStatePending {
  type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_PENDING;
}

export const setPollPassengerValidationCallStateSuccess =
  (): IPollPassengerValidationCallStateSuccess => ({
    type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_SUCCESS,
  });

export interface IPollPassengerValidationCallStateSuccess {
  type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_SUCCESS;
}

export const setPollPassengerValidationCallStateFailure = (
  errors: PaymentError[]
): IPollPassengerValidationCallStateFailure => ({
  type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_FAILED,
  errors,
});

export interface IPollPassengerValidationCallStateFailure {
  type: actionTypes.POLL_PASSENGER_VALIDATION_CALL_STATE_FAILED;
  errors: PaymentError[];
}

export const setPassengersValid = (): ISetPassengersValid => ({
  type: actionTypes.SET_PASSENGERS_VALID,
});

export interface ISetPassengersValid {
  type: actionTypes.SET_PASSENGERS_VALID;
}

export const setPassengersInvalid = (
  errors: PassengerError[]
): ISetPassengersInvalid => ({
  type: actionTypes.SET_PASSENGERS_INVALID,
  errors,
});

export interface ISetPassengersInvalid {
  type: actionTypes.SET_PASSENGERS_INVALID;
  errors: PassengerError[];
}

export const scheduleBook = ({
  agentFee,
  approvalRequestReason,
  initialTime,
}: {
  agentFee?: number;
  approvalRequestReason?: string;
  initialTime?: string;
}): ISetScheduleBook => ({
  type: actionTypes.SCHEDULE_BOOK,
  agentFee,
  approvalRequestReason,
  initialTime,
});

export interface ISetScheduleBook {
  type: actionTypes.SCHEDULE_BOOK;
  agentFee?: number;
  approvalRequestReason?: string;
  initialTime?: string;
}

export const setScheduleBookSuccess = (): ISetScheduleBookSuccess => ({
  type: actionTypes.SET_SCHEDULE_BOOK_SUCCESS,
});

export interface ISetScheduleBookSuccess {
  type: actionTypes.SET_SCHEDULE_BOOK_SUCCESS;
}

export const setScheduleBookFailed = (
  invalid: Invalid
): ISetScheduleBookFailed => ({
  type: actionTypes.SET_SCHEDULE_BOOK_FAILED,
  invalid,
});

export interface ISetScheduleBookFailed {
  type: actionTypes.SET_SCHEDULE_BOOK_FAILED;
  invalid: Invalid;
}

export const pollConfirmationDetails = (
  payload: PaymentSessionToken,
  agentFee?: number,
  initialTime?: string
): IPollConfirmationDetails => ({
  type: actionTypes.POLL_CONFIRMATION_DETAILS,
  payload,
  agentFee,
  initialTime,
});

export interface IPollConfirmationDetails {
  type: actionTypes.POLL_CONFIRMATION_DETAILS;
  payload: PaymentSessionToken;
  agentFee?: number;
  requestId?: string;
  initialTime?: string;
}

export const setPollConfirmationDetailsCallStatePending =
  (): IPollConfirmationDetailsCallStatePending => ({
    type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_PENDING,
  });

export interface IPollConfirmationDetailsCallStatePending {
  type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_PENDING;
}

export const setPollConfirmationDetailsCallStateSuccess =
  (): IPollConfirmationDetailsCallStateSuccess => ({
    type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_SUCCESS,
  });

export interface IPollConfirmationDetailsCallStateSuccess {
  type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_SUCCESS;
}

export const setPollConfirmationDetailsCallStateFailure = (
  errors: PaymentError[] | PurchaseError[]
): IPollConfirmationDetailsCallStateFailure => ({
  type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_FAILED,
  errors,
});

export interface IPollConfirmationDetailsCallStateFailure {
  type: actionTypes.POLL_CONFIRMATION_DETAILS_CALL_STATE_FAILED;
  errors: PaymentError[] | PurchaseError[];
}

export const setConfirmationDetails = (
  confirmationDetails: GroundBookingResult
): ISetConfirmationDetails => ({
  type: actionTypes.SET_CONFIRMATION_DETAILS,
  confirmationDetails,
});

export interface ISetConfirmationDetails {
  type: actionTypes.SET_CONFIRMATION_DETAILS;
  confirmationDetails: GroundBookingResult;
}

export const redoSearch = (): IRedoSearch => ({
  type: actionTypes.REDO_SEARCH,
});

export interface IRedoSearch {
  type: actionTypes.REDO_SEARCH;
}

export interface IPopulateCarBookQueryParams {
  type: actionTypes.POPULATE_CAR_BOOK_QUERY_PARAMS;
  history: H.History;
}

export const populateCarBookQueryParams = (
  history: H.History
): IPopulateCarBookQueryParams => ({
  type: actionTypes.POPULATE_CAR_BOOK_QUERY_PARAMS,
  history,
});

export interface ISetCarBookQueryParams {
  type: actionTypes.SET_CAR_BOOK_QUERY_PARAMS;
  history: H.History;
}

export const setCarBookQueryParams = (
  history: H.History
): ISetCarBookQueryParams => ({
  type: actionTypes.SET_CAR_BOOK_QUERY_PARAMS,
  history,
});

export interface ISetSelectedRewardsAccountReferenceId {
  type: actionTypes.SET_SELECTED_REWARDS_ACCOUNT_REFERENCE_ID;
  rewardsAccountReferenceId: string | null;
}

export const setSelectedRewardsAccountReferenceId = (
  rewardsAccountReferenceId: string | null
): ISetSelectedRewardsAccountReferenceId => ({
  type: actionTypes.SET_SELECTED_REWARDS_ACCOUNT_REFERENCE_ID,
  rewardsAccountReferenceId,
});

export interface ISetSelectedRewardsPaymentTotal {
  type: actionTypes.SET_SELECTED_REWARDS_PAYMENT_TOTAL;
  accountReferenceId: string;
  rewardsPaymentTotal: RewardsPrice | null;
  rewardsPaymentInFiatCurrency: FiatPrice | null;
  tripTotal: Prices | null;
}

export const setSelectedRewardsPaymentTotal = (
  accountReferenceId: string,
  rewardsPaymentTotal: RewardsPrice | null,
  rewardsPaymentInFiatCurrency: FiatPrice | null,
  tripTotal: Prices | null
): ISetSelectedRewardsPaymentTotal => ({
  type: actionTypes.SET_SELECTED_REWARDS_PAYMENT_TOTAL,
  accountReferenceId,
  rewardsPaymentTotal,
  rewardsPaymentInFiatCurrency,
  tripTotal,
});

export interface IFetchProductToEarn {
  type: actionTypes.FETCH_PRODUCT_TO_EARN;
}

export const fetchProductToEarn = (): IFetchProductToEarn => ({
  type: actionTypes.FETCH_PRODUCT_TO_EARN,
});

export interface ISetProductEarnValue {
  type: actionTypes.SET_PRODUCT_EARN_VALUE;
  productEarnValue: EarnResponse | null;
}

export const setEarnValue = (
  productEarnValue: EarnResponse | null
): ISetProductEarnValue => ({
  type: actionTypes.SET_PRODUCT_EARN_VALUE,
  productEarnValue,
});

export interface IFetchAllEarnForProduct {
  type: actionTypes.FETCH_ALL_EARN_FOR_PRODUCT;
}

export const fetchAllEarnForProduct = (): IFetchAllEarnForProduct => ({
  type: actionTypes.FETCH_ALL_EARN_FOR_PRODUCT,
});

export interface ISetAllEarnValues {
  type: actionTypes.SET_ALL_EARN_VALUES;
  earnValuesByRewardAcctId: { [key: string]: EarnResponse };
}

export const setAllEarnValues = (earnValuesByRewardAcctId: {
  [key: string]: EarnResponse;
}): ISetAllEarnValues => ({
  type: actionTypes.SET_ALL_EARN_VALUES,
  earnValuesByRewardAcctId,
});

export interface IResetBookErrors {
  type: actionTypes.RESET_BOOK_ERRORS;
  isPostQuote: boolean;
}

export const resetBookErrors = (isPostQuote?: boolean): IResetBookErrors => ({
  type: actionTypes.RESET_BOOK_ERRORS,
  isPostQuote: isPostQuote ?? false,
});

export interface ISetRewardsConversionFailed {
  type: actionTypes.REWARDS_CONVERSION_FAILED;
}

export const setRewardsConversionFailed = (): ISetRewardsConversionFailed => ({
  type: actionTypes.REWARDS_CONVERSION_FAILED,
});

export interface ISetHertzLoyaltyRewardsNumber {
  type: actionTypes.SET_HERTZ_LOYALTY_REWARDS_NUMBER;
  hertzLoyaltyRewardsNumber: string;
}
export const setHertzLoyaltyRewardsNumber = (
  hertzLoyaltyRewardsNumber: string
): ISetHertzLoyaltyRewardsNumber => ({
  type: actionTypes.SET_HERTZ_LOYALTY_REWARDS_NUMBER,
  hertzLoyaltyRewardsNumber,
});

export interface ISetFlightInfo {
  type: actionTypes.SET_FLIGHT_INFO;
  flightInfo: FlightInfoValues;
}
export const setFlightInfo = (
  flightInfo: FlightInfoValues
): ISetFlightInfo => ({
  type: actionTypes.SET_FLIGHT_INFO,
  flightInfo,
});

export interface IPendingLoyaltySync {
  vendorCode: string;
  loyaltyNumber: string;
}

export interface ISetPendingLoyaltySync {
  type: actionTypes.SET_PENDING_LOYALTY_SYNC;
  pendingLoyaltySync: IPendingLoyaltySync | null;
}
export const setPendingLoyaltySync = (
  pendingLoyaltySync: IPendingLoyaltySync | null
): ISetPendingLoyaltySync => ({
  type: actionTypes.SET_PENDING_LOYALTY_SYNC,
  pendingLoyaltySync,
});

export const fetchApplicableTravelWalletItems =
  (): IFetchApplicableTravelWalletItems => ({
    type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS,
  });

export interface IFetchApplicableTravelWalletItems {
  type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS;
}

export interface IFetchApplicableTravelWalletItemsCallStateSuccess {
  type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS_CALL_STATE_SUCCESS;
}

export const setFetchApplicableTravelWalletItemsCallStateSuccess =
  (): IFetchApplicableTravelWalletItemsCallStateSuccess => ({
    type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS_CALL_STATE_SUCCESS,
  });

export interface IFetchApplicableTravelWalletItemsCallStateFailure {
  type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS_CALL_STATE_FAILURE;
}

export const setFetchApplicableTravelWalletItemsCallStateFailure =
  (): IFetchApplicableTravelWalletItemsCallStateFailure => ({
    type: actionTypes.FETCH_APPLICABLE_TRAVEL_WALLET_ITEMS_CALL_STATE_FAILURE,
  });

export interface ISetTravelWalletOffers {
  type: actionTypes.SET_TRAVEL_WALLET_OFFERS;
  offers: TravelWalletOffer[];
}

export const setTravelWalletOffers = (
  offers: TravelWalletOffer[]
): ISetTravelWalletOffers => ({
  type: actionTypes.SET_TRAVEL_WALLET_OFFERS,
  offers,
});

export interface ISetOfferToApply {
  type: actionTypes.SET_OFFER_TO_APPLY;
  offer?: TravelWalletOffer;
}

export const setOfferToApply = (
  offer?: TravelWalletOffer
): ISetOfferToApply => ({
  type: actionTypes.SET_OFFER_TO_APPLY,
  offer,
});

export interface ISetBestOfferOverall {
  type: actionTypes.SET_BEST_OFFER_OVERALL;
  offer: TravelWalletOffer;
}

export const setBestOfferOverall = (
  offer: TravelWalletOffer
): ISetBestOfferOverall => ({
  type: actionTypes.SET_BEST_OFFER_OVERALL,
  offer,
});

export interface ISetTravelWalletCredit {
  type: actionTypes.SET_TRAVEL_WALLET_CREDIT;
  credit?: TravelWalletCredit;
}

export const setTravelWalletCredit = (
  credit?: TravelWalletCredit
): ISetTravelWalletCredit => ({
  type: actionTypes.SET_TRAVEL_WALLET_CREDIT,
  credit,
});

export interface ISetCreditToApply {
  type: actionTypes.SET_CREDIT_TO_APPLY;
  credit?: TravelWalletCredit;
}

export const setCreditToApply = (
  credit?: TravelWalletCredit
): ISetCreditToApply => ({
  type: actionTypes.SET_CREDIT_TO_APPLY,
  credit,
});

// Corporate Travel

export interface ISetCarBookExperiments {
  type: actionTypes.SET_CAR_BOOK_EXPERIMENTS;
  experiments: ICarBookExperiments;
}

export const setCarBookExperiments = (
  experiments: ICarBookExperiments
): ISetCarBookExperiments => ({
  type: actionTypes.SET_CAR_BOOK_EXPERIMENTS,
  experiments,
});

export const fetchCorpUserPassengers = (): IFetchCorpUserPassengers => ({
  type: actionTypes.FETCH_CORP_USER_PASSENGERS,
});

export interface IFetchCorpUserPassengers {
  type: actionTypes.FETCH_CORP_USER_PASSENGERS;
}

export const setv1FulfillSession = (
  token: PaymentSessionToken
): ISetv1FulfillSession => ({
  type: actionTypes.SET_V1_FULFILL_SESSION,
  token,
});

export interface ISetv1FulfillSession {
  type: actionTypes.SET_V1_FULFILL_SESSION;
  token: PaymentSessionToken;
}

export interface ISetTripPurpose {
  type: actionTypes.SET_TRIP_PURPOSE;
  tripPurpose: string;
}

export const setTripPurpose = (tripPurpose: string): ISetTripPurpose => ({
  type: actionTypes.SET_TRIP_PURPOSE,
  tripPurpose,
});

export interface ISetTripDescription {
  type: actionTypes.SET_TRIP_DESCRIPTION;
  tripDescription: string;
}

export const setTripDescription = (
  tripDescription: string
): ISetTripDescription => ({
  type: actionTypes.SET_TRIP_DESCRIPTION,
  tripDescription,
});

export interface ISetIsTravelApp {
  type: actionTypes.SET_IS_TRAVEL_APP;
  isTravelApp: boolean;
}

export const setIsTravelApp = (isTravelApp: boolean): ISetIsTravelApp => ({
  type: actionTypes.SET_IS_TRAVEL_APP,
  isTravelApp,
});

export type CarBookActions =
  | IFetchUserPassengers
  | IUpdateUserPassenger
  | IDeleteUserPassenger
  | ISetUserPassengers
  | ISetUserSelectedPassengerIds
  | ISetUserPassengersCallStateFailed
  | IAcknowledgeUpdateUserPassengerFailure
  | ISetContactInfo
  | IFetchPaymentMethod
  | IListPaymentMethods
  | IDeletePaymentMethod
  | ISetPaymentMethod
  | ISetPaymentMethods
  | ISetSelectedPaymentMethodId
  | IVerifyPaymentMethod
  | IVerifyPaymentMethodCallStateSuccess
  | IVerifyPaymentMethodCallStateFailed
  | IDeletePaymentMethodCallStateSuccess
  | IDeletePaymentMethodCallStateFailed
  | ISetPaymentMethodsCallStateFailed
  | ISetPaymentMethodCallStateFailed
  | ISetSession
  | IClearSession
  | ISchedulePriceQuote
  | ISetSchedulePriceQuoteCallStateSuccess
  | ISetSchedulePriceQuoteCallStateFailure
  | IPollPriceQuote
  | IPollPriceQuoteCallStateSuccess
  | ISetPollPriceQuoteCallStateFailure
  | ISetPriceQuote
  | IPollPassengerValidation
  | IPollPassengerValidationCallStatePending
  | IPollPassengerValidationCallStateSuccess
  | IPollPassengerValidationCallStateFailure
  | ISetPassengersValid
  | ISetPassengersInvalid
  | ISetScheduleBook
  | ISetScheduleBookFailed
  | ISetScheduleBookSuccess
  | IPollConfirmationDetails
  | IPollConfirmationDetailsCallStatePending
  | IPollConfirmationDetailsCallStateSuccess
  | IPollConfirmationDetailsCallStateFailure
  | ISetConfirmationDetails
  | IRedoSearch
  | IPopulateCarBookQueryParams
  | ISetCarBookQueryParams
  | ISetSelectedRewardsAccountReferenceId
  | ISetSelectedRewardsPaymentTotal
  | ISetProductEarnValue
  | IFetchAllEarnForProduct
  | ISetAllEarnValues
  | IAcknowledgePriceDifference
  | IResetBookErrors
  | ISetRewardsConversionFailed
  | ISetHertzLoyaltyRewardsNumber
  | ISetFlightInfo
  | ISetPendingLoyaltySync
  | ISetTravelWalletOffers
  | IFetchApplicableTravelWalletItems
  | IFetchApplicableTravelWalletItemsCallStateSuccess
  | IFetchApplicableTravelWalletItemsCallStateFailure
  | ISetBestOfferOverall
  | ISetOfferToApply
  | IResetPaymentCardSelectedAccounts
  | ISetTravelWalletCredit
  | ISetCreditToApply
  | ISetCarBookExperiments
  | IFetchCorpUserPassengers
  | ISetv1FulfillSession
  | ISetTripPurpose
  | ISetTripDescription
  | ISetIsTravelApp;
